import React, { FC, Fragment, Suspense, lazy, useMemo, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Divider, Typography } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import classNames from 'classnames';
import { IComment } from './constants';
import { useVideoFeedback } from './useVideoFeedback';
import { CommentList } from './CommentList';
import { canViewPreviousVersions } from './canViewPreviousVersions';
import { IconChatMsgs } from '../../../../utils/icons';
import eventEmitter from '../../../../utils/eventEmitter';
import { useUser } from '../../../../hooks/useUser';
import axiosInstant from '../../../../api';
import { getProjectFileVersions } from '../../../../api/endpoints';
import { DotLottleLoader } from '../../../../components/Loader';

const PreviousVersionsModal = lazy(() =>
  // eslint-disable-next-line import/no-cycle
  import('./PreviousVersionsModal').then((m) => ({ default: m.PreviousVersionsModal }))
);

interface CommentsListPanelProps {
  video: any;
}
export const CommentsListPanel: FC<CommentsListPanelProps> = () => {
  const { role } = useUser();
  const commentsPanelRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const focusCommentId = searchParams.get('comment');
  // The ?comment= id last scrolled to. An id, not a one-shot flag: the
  // Downloads tab keeps this modal mounted when ?review= swaps the video
  // (the "Applied to" menu), and the new thread's comment must focus too.
  const focusedIdRef = useRef<string | null>(null);
  const { video, project, comments, onFeedbackSubmit, getComments, isCommentLoading, isPublicPage, readOnly } =
    useVideoFeedback();
  const [earlierVersions, setEarlierVersions] = useState<any[] | null>(null);
  const [showPreviousVersions, setShowPreviousVersions] = useState(false);
  const projectId = project?.id || video?.projectId;
  const canSeeEarlier =
    !isPublicPage && !readOnly && canViewPreviousVersions(role, video);
  const earlierCount = (earlierVersions || []).reduce(
    (sum: number, version: any) => sum + (version.comment_count || 0),
    0
  );

  useEffect(() => {
    let cancelled = false;
    setEarlierVersions(null);
    if (canSeeEarlier && projectId && video?.id) {
      axiosInstant
        .get(getProjectFileVersions(projectId, video.id))
        .then(({ data }) => {
          if (!cancelled) setEarlierVersions(data?.versions || []);
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, [canSeeEarlier, projectId, video?.id]);


  useEffect(() => {
    if (!video?.id) return undefined;

    const handleProjectMessage = () => {
      getComments(video.id);
    };

    eventEmitter.on('projectMessage', handleProjectMessage);
    return () => {
      eventEmitter.off('projectMessage', handleProjectMessage);
    };
  }, [video?.id, getComments]);

  const commentsArray = useMemo(() => {
    const groups: Record<string, IComment[]> = {};
    comments.forEach((comment) => {
      if (!comment.created_at) return;
      const dateKey = new Date(comment.created_at).toISOString().split('T')[0];
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(comment);
    });
    return Object.entries(groups).map(([date, commentsArr]) => ({
      date,
      commentsArr,
    }));
  }, [comments]);

  useEffect(() => {
    if (focusCommentId && focusedIdRef.current !== focusCommentId) return undefined;
    const id = window.setTimeout(() => {
      commentsPanelRef.current?.scrollTo({
        top: commentsPanelRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }, 300);
    return () => window.clearTimeout(id);
  }, [comments, focusCommentId]);

  useEffect(() => {
    if (!focusCommentId || focusedIdRef.current === focusCommentId || !comments.length) return undefined;
    const id = window.setTimeout(() => {
      // Look inside this panel only: another review could be mounted too.
      const panel = commentsPanelRef.current;
      // Escaped: ?comment= comes from the URL and could break the selector.
      const el = panel?.querySelector<HTMLElement>(`#review-comment-${CSS.escape(focusCommentId)}`);
      if (!el || !panel) return;
      focusedIdRef.current = focusCommentId;
      // Centre it within the comments panel only — scrollIntoView would also
      // scroll the review modal and cut off the video title above.
      const offset = el.getBoundingClientRect().top - panel.getBoundingClientRect().top;
      panel.scrollTo({
        top: panel.scrollTop + offset - (panel.clientHeight - el.offsetHeight) / 2,
        behavior: 'smooth',
      });
    }, 350);
    return () => window.clearTimeout(id);
  }, [focusCommentId, comments]);

  return (
    <Box className="border border-border-token rounded-2xl py-4 flex flex-col relative">
      <Box className="px-4 pb-4 border-b border-border-token">
        <Typography variant="h6" className='!font-semibold'>
          Comments
        </Typography>
      </Box>
      {/* Above the thread, so it's seen before scrolling (TDC-1733). */}
      {earlierCount > 0 && (
        <Box className="px-4 py-3 border-b border-border-token">
          <button
            type="button"
            onClick={() => setShowPreviousVersions(true)}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-xl border border-border-token bg-surface-2 transition-colors hover:bg-tint hover:border-accent"
          >
            <span className="shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-tint text-accent-text">
              <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />
            </span>
            <span className="flex flex-col min-w-0 flex-1 gap-0.5">
              <span className="text-[12.5px] font-semibold text-text-1 leading-tight">
                {earlierCount} comment{earlierCount === 1 ? '' : 's'} on earlier versions
              </span>
              <span className="text-[11.5px] text-text-2 leading-tight">View previous versions</span>
            </span>
            <ChevronRightIcon className="shrink-0" sx={{ fontSize: 18, color: 'var(--muted)' }} />
          </button>
        </Box>
      )}
      <Box
        ref={commentsPanelRef}
        className={classNames(
          'border-border-token overflow-auto h-[595px] bg-surface-2 relative',
          {
            'h-[513px]': onFeedbackSubmit,
          }
        )}
      >
        {!commentsArray.length && !isCommentLoading && (
          <Box className="h-full w-full flex flex-col items-center justify-center gap-6">
            <IconChatMsgs />
            <Typography className="text-text-2">
              There are no comments yet
            </Typography>
          </Box>
        )}
        {commentsArray.map((list, index) => (
          <Fragment key={index}>
            {index !== 0 && <Divider />}
            <CommentList commentList={list} focusCommentId={focusCommentId} />
          </Fragment>
        ))}
      </Box>

      {showPreviousVersions && projectId && (
        <Suspense fallback={null}>
          <PreviousVersionsModal
            projectId={projectId}
            file={video}
            project={project}
            initialVersions={earlierVersions || undefined}
            onClose={() => setShowPreviousVersions(false)}
          />
        </Suspense>
      )}

      {isCommentLoading && (
        <Box className="absolute inset-0 bg-white bg-opacity-30 backdrop-blur-sm z-[9999] flex items-center justify-center rounded-2xl">
          <DotLottleLoader />
        </Box>
      )}
      {onFeedbackSubmit && (
        <Box className="border-t border-border-token p-6 pb-2 bg-surface rounded-bl-2xl rounded-br-2xl" />
      )}
    </Box>
  );
};
